import type { Account, AppState, BankLine, InvoiceLine, Journal, JournalLine, SetupConfig } from "./types";

export const STORAGE_KEY = "ledzerly-books-uat-v1";

export const today = () => new Date().toISOString().slice(0, 10);

export const uid = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const formatINR = (value: number, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${currency} ${Math.round(value).toLocaleString("en-IN")}`;
  }
};

export const formatNumber = (value: number) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(value);

export const formatDate = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const initials = (value: string) => value.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();

export const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

export const lineTotal = (line: InvoiceLine) => line.quantity * line.rate;
export const invoiceTax = (lines: InvoiceLine[]) => sum(lines.map((line) => ["Exempt", "Out of scope", "RCM"].includes(line.taxTreatment) ? 0 : lineTotal(line) * (line.taxRate / 100)));
export const invoiceSubtotal = (lines: InvoiceLine[]) => sum(lines.map(lineTotal));

export const downloadFile = (name: string, content: string, type = "text/plain;charset=utf-8") => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const csvEscape = (value: unknown) => {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

export const toCSV = (headers: string[], rows: Array<Array<unknown>>) => [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");

export const parseCSV = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];
    if (char === '"' && quoted && next === '"') {
      cell += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      row.push(cell.trim());
      cell = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell.trim());
      if (row.some((value) => value.length > 0)) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  if (cell.length || row.length) {
    row.push(cell.trim());
    if (row.some((value) => value.length > 0)) rows.push(row);
  }
  return rows;
};

export const parseAmount = (value: string | undefined) => Number(String(value ?? "0").replaceAll(",", "").replace(/[₹$()]/g, "")) || 0;

export const bankLineToJournal = (line: BankLine, setup: SetupConfig, accountByCode: Map<string, Account>): Journal => {
  const isDeposit = line.credit > 0;
  const amount = isDeposit ? line.credit : line.debit;
  const suggestedCode = line.suggestedAccount || (isDeposit ? "1200" : "6000");
  const findCode = (code: string) => accountByCode.get(code) ?? Array.from(accountByCode.values()).find((account) => account.code.endsWith(`-${code}`));
  const suggested = findCode(suggestedCode) ?? findCode("6000");
  const bankAccount = findCode("1110") ?? findCode("1100");
  const lines: JournalLine[] = isDeposit
    ? [
        { id: uid("jl"), accountCode: bankAccount?.code ?? "1110", accountName: bankAccount?.name ?? "HDFC Bank", debit: amount, credit: 0, analyticGroup: "Banking" },
        { id: uid("jl"), accountCode: suggested?.code ?? "1200", accountName: suggested?.name ?? "Trade receivables", debit: 0, credit: amount, analyticGroup: "Banking" }
      ]
    : [
        { id: uid("jl"), accountCode: suggested?.code ?? "6000", accountName: suggested?.name ?? "Operating expenses", debit: amount, credit: 0, analyticGroup: "Banking" },
        { id: uid("jl"), accountCode: bankAccount?.code ?? "1110", accountName: bankAccount?.name ?? "HDFC Bank", debit: 0, credit: amount, analyticGroup: "Banking" }
      ];
  return {
    id: uid("jnl"),
    number: `JV-${String(Date.now()).slice(-6)}`,
    date: line.date || today(),
    narration: line.description,
    source: "Bank statement upload",
    status: "Pending review",
    amount,
    currency: setup.baseCurrency,
    lines,
    maker: "System suggestion",
    createdAt: new Date().toISOString(),
    sourceDocument: line.reference
  };
};

export const findBusinessKey = (nature: string) => {
  const value = nature.toLowerCase();
  if (/(ready mix|rmc|manufactur|construction material|concrete)/.test(value)) return "rmc";
  if (/(trading|wholesale|retail|distribution)/.test(value)) return "trading";
  if (/(software|saas|technology|it services)/.test(value)) return "technology";
  if (/(professional|consult|ca firm|accounting|legal)/.test(value)) return "professional";
  if (/(edtech|education|coaching|training)/.test(value)) return "edtech";
  return "generic";
};

export const businessLabel = (key: string) => ({ rmc: "RMC / manufacturing", trading: "Trading / distribution", technology: "Technology / SaaS", professional: "Professional services", edtech: "EdTech / training", generic: "General business" }[key] ?? "General business");

export const createBankLine = (overrides: Partial<BankLine> = {}): BankLine => ({
  id: uid("bank"),
  date: today(),
  description: "Imported bank transaction",
  reference: `UPL-${String(Date.now()).slice(-6)}`,
  debit: 0,
  credit: 0,
  balance: 0,
  status: "Unreconciled",
  source: "CSV upload",
  ...overrides
});

export const loadState = (fallback: AppState): AppState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as AppState : fallback;
  } catch {
    return fallback;
  }
};

export const saveState = (state: AppState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // The application remains usable if local storage is disabled or full.
  }
};
